import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router, NavigationEnd } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import { filter, flatMap, takeUntil } from 'rxjs/operators';

import {
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  Template,
  FormViews,
} from '@site-mate/dashpivot-shared-library';

import { TemplatesService } from 'app/apps/templates.service';
import { FormService } from 'app/form/form.service';
import { SegmentService } from 'app/segment/segment.service';
import { TeamWeb } from 'app/shared/model/team.model';
import { TemplateRouteContext } from 'app/shared/model/template-route-context.model';
import { AppUtilService } from 'app/shared/service/app-util.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { TeamService } from 'app/shared/service/team.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-work-flow',
  templateUrl: 'work-flow.component.html',
  styleUrls: ['work-flow.component.scss'],
})
export class WorkFlowComponent implements OnInit, OnDestroy {
  workflowHeaders;
  channels = [];
  template;
  loading = true;
  teamUsers;
  hasForms: boolean;
  private unSubscribe = new Subject<void>();
  workflowColumnVisible = true;

  @Input()
  context: TemplateRouteContext;

  @Input()
  canAddForm: boolean;

  constructor(
    public readonly appUtilService: AppUtilService,
    private readonly formService: FormService,
    private readonly templatesService: TemplatesService,
    private readonly toastrService: ToastrService,
    private readonly userService: UserService,
    private readonly teamService: TeamService,
    private readonly route: ActivatedRoute,
    private readonly errorHandler: ErrorHandler,
    private readonly location: Location,
    private readonly router: Router,
    private readonly segmentService: SegmentService,
    private readonly routeParamsService: RouteParamsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initWorkflow();
    this.setEmailRedirection();
    this.setNotificationOptions();
    this.initReloadListener();
  }

  initReloadListener() {
    // fetch data on page reload
    this.router.events
      .pipe(
        takeUntil(this.unSubscribe),
        filter((event) => event instanceof NavigationEnd),
        filter(() => {
          const { state } = this.router.getCurrentNavigation().extras;
          return state && state.reload;
        }),
      )
      .subscribe(() => {
        this.initChannels();
        this.reinitialiseWorkflowColumn();
        this.loadFormsCount();
      });
  }

  setEmailRedirection() {
    this.route.queryParamMap.pipe(takeUntil(this.unSubscribe)).subscribe((params: Params) => {
      if (params.get('fromEmail')) {
        this.location.go(this.router.createUrlTree([], { relativeTo: this.route }).toString());
        void EventNotifierService.notify(
          new DashpivotEvent(EventTypes.TemplateWorkflowEmailLinkClicked),
          this.segmentService,
        );
      }
    });
  }

  setNotificationOptions() {
    this.teamService.currentTeam
      .pipe(
        takeUntil(this.unSubscribe),
        filter((team: TeamWeb) => team && team.id && team.isTeam),
        flatMap((team: TeamWeb) => this.teamService.getUsers(team.id)),
      )
      .subscribe(
        (users: any[]) => {
          this.teamUsers = users
            .map((user) => ({
              id: user._id,
              firstName: user.firstName,
              lastName: user.lastName,
              fullName: user.fullName,
              email: user.email,
              label: user.fullName,
              value: user.email,
              isActivated: user.isActivated,
              avatar: user.avatar,
            }))
            .sort((a, b) => b.isActivated - a.isActivated || a.label?.localeCompare(b.label));
        },
        (error) => this.errorHandler.handle(error),
      );
  }

  initWorkflow() {
    this.routeParamsService
      .get('template')
      .pipe(
        takeUntil(this.unSubscribe),
        filter((template) => !!template),
      )
      .subscribe((template: Template) => {
        this.template = template;
        if (template.columns && template.columns.length) {
          this.workflowHeaders = template.columns;
          this.initChannels();
        }
        this.loadFormsCount();
      });
  }

  initChannels(): void {
    if (!this.workflowHeaders) {
      return;
    }

    this.channels = this.workflowHeaders.map(() => ({ totalFormsCount: null }));
  }

  loadFormsCount(): void {
    this.loading = true;

    forkJoin(this.formCountRequests())
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        (formsCount: any[]) => {
          this.loading = false;
          this.channels.forEach((channel, index) => {
            const count = formsCount[index][0];
            channel.totalFormsCount = count?.totalFormsCount || 0;
          });

          this.hasForms = this.channels.some(
            (channel) => channel.totalFormsCount && channel.totalFormsCount > 0,
          );
        },
        (error) => {
          this.loading = false;
          this.hasForms = true; // Temporary masking of issue - should be removing after debugging.
          this.errorHandler.handle(error, {
            error,
            channels: this.channels,
          });
        },
      );
  }

  private formCountRequests() {
    return this.channels.map((_, columnNumber) =>
      this.formService.listTemplateForms(this.template.id, {
        workflowStep: columnNumber + 1,
        formCount: true,
        view: FormViews.Workflow,
      }),
    );
  }

  // This recreates the workflow columns component
  // This is needed to make the infinite scroll to work correctly after updating the forms
  private reinitialiseWorkflowColumn(): void {
    this.workflowColumnVisible = false;
    this.changeDetectorRef.detectChanges();
    this.workflowColumnVisible = true;
  }

  updateNotificationListInTemplate({ notificationList, columnIndex }) {
    this.template.columns[columnIndex].notificationList = notificationList;
    this.template = { ...this.template };
  }

  updateNotificationList({ notificationList, columnIndex }) {
    this.templatesService
      .updateWorkflowNotificationList(this.template.id, columnIndex, notificationList)
      .subscribe({
        next: () => {
          this.toastrService.successByKey('workflowNotificationListUpdated');
          this.updateNotificationListInTemplate({ notificationList, columnIndex });
        },
        error: (error) => this.errorHandler.handle(error),
      });
  }

  get canEditNotificationList(): boolean {
    return this.userService.isTeamControllerOrAbove();
  }

  handleNotificationListClick(): void {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.TemplateWorkflowNotificationBellClicked),
      this.segmentService,
    );
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
